import { LatLngLiteral } from "leaflet";

import { fetcher } from "./axiosHVS";

export interface AddressComponent {
  /**
   * The full text of the address component
   */
  longName: string;
  /**
   * The abbreviated, short text of the given address component
   */
  shortName: string;
  /**
   * An array of strings denoting the type of this address component. A list
   * of valid types can be found <a
   * href="https://developers.google.com/maps/documentation/javascript/geocoding#GeocodingAddressTypes">here</a>
   */
  types: string[];
}

export interface ViewportBounds {
  /**
   * Returns the north-east corner of this bounds.
   */
  northeast: google.maps.LatLng;
  /**
   * Returns the south-west corner of this bounds.
   */
  southwest: google.maps.LatLng;
}

export interface GeocoderResult {
  /**
   * An array of <code>GeocoderAddressComponent</code>s
   */
  components: AddressComponent[];
  /**
   * The latitude/longitude coordinates of this result
   */
  coordinates: LatLngLiteral;
  /**
   * A string containing the human-readable address of this location.
   */
  formattedAddress: string;
  /**
   * Whether the geocoder did not return an exact match for the original
   * request, though it was able to match part of the requested address. If an
   * exact match, the value will be <code>undefined</code>.
   */
  isPartialMatch?: boolean | undefined;
  /**
   * The type of location returned in <code>coordinates</code>
   */
  locationType: string;
  /**
   * The place ID associated with the location. Place IDs uniquely identify a
   * place in the Google Places database and on Google Maps. Learn more about
   * <a
   * href="https://developers.google.com/maps/documentation/places/web-service/place-id">Place
   * IDs</a> in the Places API developer guide.
   */
  placeId: string;
  provider: "google";
  /**
   * The type of the returned geocoded element.
   * For a list of possible strings, refer to the <a href=
   * "https://developers.google.com/maps/documentation/javascript/geocoding#GeocodingAddressTypes">
   * Address Component Types</a> section of the Developer&#39;s Guide.
   */
  type: string;
  /**
   * The bounds of the recommended viewport for displaying this
   * <code>GeocoderResult</code>
   */
  viewport: ViewportBounds;
}

export interface GeocodingApiResponse {
  data: GeocoderResult;
  ok: boolean;
  totalCount: number;
}

export const geocodeLatLng = async (
  coordinates?: LatLngLiteral
): Promise<GeocodingApiResponse | null> => {
  if (!coordinates) {
    return null;
  }

  return fetcher<GeocodingApiResponse>(
    `/services/geocoding?latlon=${coordinates.lat},${coordinates.lng}`
  );
};

export const geocodeAddress = async (
  address?: string
): Promise<GeocodingApiResponse | null> => {
  if (!address) {
    return null;
  }

  return fetcher<GeocodingApiResponse>(
    `/services/geocoding?address=${address}`
  );
};
